/**
 * NavSectionPopover - Hover popover for displaying child navigation items
 *
 * Shows child navigation items in a popover when hovering over section icons
 * in collapsed navigation mode.
 */

import { HoverCard, NavLink, Stack, Text } from '@mantine/core';
import { ProtectedComponent } from '@components/ProtectedComponent';
import { Link } from "@tanstack/react-router";
import { NavItem } from "./NavigationItems";

interface NavSectionPopoverProps {
  /** The section item with children to display */
  item: NavItem;
  /** Whether each child item is active */
  getChildActiveState: (child: NavItem) => boolean;
  /** The trigger element (section button) */
  children: React.ReactNode;
}

export const NavSectionPopover = ({item, getChildActiveState, children}: NavSectionPopoverProps) => {
  if (!item.children || item.children.length === 0) {
    return <>{children}</>;
  }

  return (
    <HoverCard shadow="md" position="right" withArrow openDelay={500}>
      <HoverCard.Target>
        {children}
      </HoverCard.Target>
      <HoverCard.Dropdown p="xs">
        <Stack gap={4}>
          <Text size="sm" fw={600} c="dimmed" px="xs">
            {item.label}
          </Text>
          {item.children.map(child => {
            const isActive = getChildActiveState(child);
            const navLink = (
              <NavLink
                key={child.label}
                component={child.path ? Link : undefined}
                to={child.path}
                label={child.label}
                leftSection={child.icon}
                active={isActive}
                style={{whiteSpace: 'nowrap'}}
              />
            );

            return child.requiredRole ? (
              <ProtectedComponent key={child.label} requiredRole={child.requiredRole}>
                {navLink}
              </ProtectedComponent>
            ) : navLink;
          })}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};